import RecordedSportStats from "../components/RecordedSportStats";
// src/pages/NhlGameDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API_BASE } from "../api";
import { isAllowedPublicEdge } from "../utils/publicEdge";
import MatchupModelSection from "../components/MatchupModelSection";
const api = (p) => `${API_BASE}${p}`;

/* ── colour palette ────────────────────────────────────────── */
const C = {
  pageBg:     "#eef6fa",          // faint ice-blue page wash
  cardBg:     "#ffffff",
  rinkLine:   "#c2dfe8",          // rink outline / creases
  rinkAccent: "#a8d5e2",          // goal nets, faceoff rings
  blueLine:   "#60a5fa",          // NHL blue lines
  redLine:    "#f87171",          // center red line / dots
  accentDark: "#0e7490",          // deep teal – headings, active tab
  accentLight:"#e0f2fe",          // ice-blue pill / badge bg
  textPri:    "#1e293b",
  textSub:    "#475569",
  textMut:    "#94a3b8",
  green:      "#16a34a",
  greenLt:    "rgba(22,163,74,0.10)",
  red:        "#dc2626",
  redLt:      "rgba(220,38,38,0.10)",
};

/* ── SVG: full-rink background (fixed, behind everything) ──── */
function RinkBg() {
  return (
    <svg
      viewBox="0 0 720 320"
      preserveAspectRatio="xMidYMid slice"
      style={{
        position: "fixed", inset: 0, width: "100%", height: "100%",
        opacity: 0.18, pointerEvents: "none", zIndex: 0,
      }}
    >
      {/* outer boards – rounded rectangle */}
      <rect x="30" y="30" width="660" height="260" rx="40" ry="40"
            fill="none" stroke={C.rinkLine} strokeWidth="3.5" />

      {/* ── goal creases (blue) ── */}
      {/* left crease */}
      <path d="M30,125 Q72,125 72,160 Q72,195 30,195"
            fill="none" stroke={C.rinkAccent} strokeWidth="2.2" />
      {/* right crease */}
      <path d="M690,125 Q648,125 648,160 Q648,195 690,195"
            fill="none" stroke={C.rinkAccent} strokeWidth="2.2" />

      {/* ── goals (nets) ── */}
      <rect x="12" y="140" width="18" height="40" rx="3"
            fill="none" stroke={C.rinkAccent} strokeWidth="2" />
      <rect x="690" y="140" width="18" height="40" rx="3"
            fill="none" stroke={C.rinkAccent} strokeWidth="2" />

      {/* ── blue lines ── */}
      <line x1="262" y1="30" x2="262" y2="290" stroke={C.blueLine} strokeWidth="2.8" />
      <line x1="458" y1="30" x2="458" y2="290" stroke={C.blueLine} strokeWidth="2.8" />

      {/* ── center red line (dashed) ── */}
      <line x1="360" y1="30" x2="360" y2="290"
            stroke={C.redLine} strokeWidth="1.8" strokeDasharray="16 9" />

      {/* ── center circle + dot ── */}
      <circle cx="360" cy="160" r="44" fill="none" stroke={C.redLine} strokeWidth="1.8" />
      <circle cx="360" cy="160" r="4"  fill={C.redLine} />

      {/* ── zone faceoff circles ── left offensive */}
      <circle cx="160" cy="105" r="25" fill="none" stroke={C.rinkLine} strokeWidth="1.2" />
      <circle cx="160" cy="105" r="3"  fill={C.rinkLine} />
      <circle cx="160" cy="215" r="25" fill="none" stroke={C.rinkLine} strokeWidth="1.2" />
      <circle cx="160" cy="215" r="3"  fill={C.rinkLine} />

      {/* ── zone faceoff circles ── right offensive */}
      <circle cx="560" cy="105" r="25" fill="none" stroke={C.rinkLine} strokeWidth="1.2" />
      <circle cx="560" cy="105" r="3"  fill={C.rinkLine} />
      <circle cx="560" cy="215" r="25" fill="none" stroke={C.rinkLine} strokeWidth="1.2" />
      <circle cx="560" cy="215" r="3"  fill={C.rinkLine} />

      {/* ── neutral-zone dots (on the blue lines) ── */}
      <circle cx="262" cy="105" r="3" fill={C.rinkLine} />
      <circle cx="262" cy="215" r="3" fill={C.rinkLine} />
      <circle cx="458" cy="105" r="3" fill={C.rinkLine} />
      <circle cx="458" cy="215" r="3" fill={C.rinkLine} />
    </svg>
  );
}

/* ── SVG: hockey puck (used in header between team names) ──── */
function Puck({ size = 24 }) {
  return (
    <svg width={size} height={size * 0.55} viewBox="0 0 24 14" style={{ flexShrink: 0 }}>
      {/* shadow / side */}
      <ellipse cx="12" cy="10" rx="10" ry="3.5" fill="#374151" />
      {/* top face */}
      <ellipse cx="12" cy="7"  rx="10" ry="3.5" fill="#1e293b" />
    </svg>
  );
}

/* ── helpers ──────────────────────────────────────────────── */
const fmtDate = (iso) => {
  if (!iso) return "—";
  const safe = iso.endsWith("Z") ? iso : iso + "Z";
  try {
    return new Date(safe).toLocaleString("en-GB", {
      timeZone: "Europe/London",
      weekday: "short", day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const TABS = ["Preview", "Edges", "Odds", "Stats", "Lineups"];

/* ── shared sub-components ────────────────────────────────── */
function SectionHeading({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: C.accentDark,
      textTransform: "uppercase", letterSpacing: 1, marginBottom: 12,
    }}>
      {children}
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div style={{ fontSize: 14, color: C.textMut, textAlign: "center", padding: "40px 0" }}>
      {text}
    </div>
  );
}

/* ── main page ────────────────────────────────────────────── */
export default function NhlGameDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err,  setErr]  = useState("");
  const [tab,  setTab]  = useState("Preview");

  useEffect(() => {
    setErr("");
    setData(null);
    fetch(api(`/api/fixtures/id/${id}/json`), { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(setData)
      .catch((e) => {
        console.error(e);
        setErr(e.message);
      });
  }, [id]);

  /* group odds by bookmaker */
  const groupedOdds = useMemo(() => {
    const byBook = {};
    for (const o of data?.odds || []) {
      const b = (o.bookmaker || "other").toLowerCase();
      (byBook[b] = byBook[b] || []).push(o);
    }
    Object.values(byBook).forEach((arr) =>
      arr.sort((a, b) => (a.market || "").localeCompare(b.market || ""))
    );
    return byBook;
  }, [data]);

  /* ── guards ── */
  if (err)
    return (
      <div style={{ padding: 48, textAlign: "center", color: C.red, fontWeight: 600 }}>
        {err}
      </div>
    );
  if (!data)
    return (
      <div style={{ padding: 48, textAlign: "center", color: C.textMut }}>
        Loading…
      </div>
    );
  if (!data.fixture)
    return (
      <div style={{ padding: 48, textAlign: "center", color: C.textMut }}>
        No fixture data found.
      </div>
    );

  const f     = data.fixture;
  const edges = (data.best_edges || []).filter((edge) => isAllowedPublicEdge(edge?.edge));
  const finished =
    f.full_time_home != null && f.full_time_away != null;

  /* ─────────────────────── RENDER ─────────────────────── */
  return (
    <div style={{ background: C.pageBg, minHeight: "100vh", position: "relative" }}>
      {/* rink lives behind everything */}
      <RinkBg />

      {/* ── scrollable content layer ── */}
      <div style={{
        position: "relative", zIndex: 1,
        maxWidth: 780, margin: "0 auto", padding: "16px 16px 48px",
      }}>

        {/* back nav */}
        <Link
          to="/nhl"
          style={{ color: C.accentDark, fontSize: 13, textDecoration: "none", fontWeight: 600 }}
        >
          ← Back to NHL
        </Link>

        {/* ────── HEADER CARD ────── */}
        <div style={{
          background: C.cardBg, borderRadius: 20,
          padding: "22px 20px 18px", marginTop: 14,
          boxShadow: "0 3px 16px rgba(14,116,144,0.12)",
          border: "1px solid rgba(14,116,144,0.10)",
        }}>
          {/* status badge row */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
            {finished ? (
              <span style={{
                background: "#f1f5f9", color: C.textSub,
                fontSize: 11, fontWeight: 700, letterSpacing: 0.8,
                padding: "3px 12px", borderRadius: 999, textTransform: "uppercase",
              }}>
                Final
              </span>
            ) : (
              <span style={{
                background: C.accentLight, color: C.accentDark,
                fontSize: 11, fontWeight: 700, letterSpacing: 0.8,
                padding: "3px 12px", borderRadius: 999, textTransform: "uppercase",
              }}>
                Upcoming
              </span>
            )}
          </div>

          {/* team matchup row */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 12, flexWrap: "wrap",
          }}>
            {/* home: logo + name */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end", flex: "1 1 auto", minWidth: 100 }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: C.textPri, textAlign: "right" }}>
                {f.home_team}
              </span>
              {f.home_logo ? (
                <img src={f.home_logo} alt={f.home_team} width={42} height={42}
                  style={{ objectFit: "contain" }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : <Puck size={30} />}
            </div>

            {/* centre: score (finished) or vs (upcoming) */}
            {finished ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 30, fontWeight: 800, color: C.textPri }}>{f.full_time_home}</span>
                <span style={{ fontSize: 18, color: C.textMut, fontWeight: 600 }}>–</span>
                <span style={{ fontSize: 30, fontWeight: 800, color: C.textPri }}>{f.full_time_away}</span>
              </div>
            ) : (
              <span style={{ fontSize: 14, fontWeight: 700, color: C.textMut }}>vs</span>
            )}

            {/* away: logo + name */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-start", flex: "1 1 auto", minWidth: 100 }}>
              {f.away_logo ? (
                <img src={f.away_logo} alt={f.away_team} width={42} height={42}
                  style={{ objectFit: "contain" }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : <Puck size={30} />}
              <span style={{ fontSize: 20, fontWeight: 700, color: C.textPri, textAlign: "left" }}>
                {f.away_team}
              </span>
            </div>
          </div>

          {/* meta chips */}
          <div style={{
            display: "flex", justifyContent: "center", gap: 18,
            marginTop: 12, flexWrap: "wrap",
          }}>
            <span style={{ fontSize: 13, color: C.textMut, display: "flex", alignItems: "center", gap: 5 }}>
              🏒 <strong style={{ color: C.textSub }}>{f.comp || "NHL"}</strong>
            </span>
            <span style={{ fontSize: 13, color: C.textMut, display: "flex", alignItems: "center", gap: 5 }}>
              🕐 {fmtDate(f.kickoff_utc)}
            </span>
          </div>
        </div>

        {/* ────── TAB BAR ────── */}
        <div style={{
          display: "flex", gap: 2, marginTop: 18, flexWrap: "wrap",
          borderBottom: "2px solid #e2e8f0",
        }}>
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                padding: "9px 18px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: tab === t ? 700 : 500,
                color: tab === t ? C.accentDark : C.textMut,
                borderBottom: tab === t
                  ? `3px solid ${C.accentDark}`
                  : "3px solid transparent",
                marginBottom: -2,           // overlap the container border
                borderRadius: "6px 6px 0 0",
                transition: "color 0.15s, border-color 0.15s",
                outline: "none",
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {/* ────── TAB CONTENT CARD ────── */}
        <div style={{
          background: C.cardBg, borderRadius: "0 16px 16px 16px",
          padding: 24,
          boxShadow: "0 2px 10px rgba(14,116,144,0.08)",
          border: "1px solid rgba(14,116,144,0.10)",
          borderTop: "none",
        }}>

          {/* ═══════════ PREVIEW ═══════════ */}
          {tab === "Preview" && (
            <div>
              <RecordedSportStats fixtureId={id} sport="nhl" />
        <MatchupModelSection
                fixtureId={id}
                sport="nhl"
                fixture={f}
                homeStats={data.home_stats}
                awayStats={data.away_stats}
                predictions={data.predictions}
                modelProbs={data.model_probs}
                theme={{
                  accent: C.accentDark,
                  accentSoft: C.accentLight,
                  accentBorder: "rgba(14,116,144,0.18)",
                  surface: "rgba(255,255,255,0.94)",
                  text: C.textPri,
                  textMuted: C.textSub,
                  border: "rgba(14,116,144,0.14)",
                  positive: C.green,
                  negative: C.red,
                }}
              />

              {/* top-3 edges summary cards */}
              {edges.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <SectionHeading>Best Edges</SectionHeading>
                  {edges.slice(0, 3).map((e, i) => {
                    const pct = (Number(e.edge) * 100).toFixed(1);
                    return (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "11px 14px",
                          background: C.greenLt,
                          borderRadius: 10,
                          borderLeft: `4px solid ${C.green}`,
                          marginBottom: 7,
                        }}
                      >
                        <div>
                          <span style={{ fontSize: 14, fontWeight: 600, color: C.textPri }}>
                            {e.market}
                          </span>
                          <span style={{ fontSize: 12, color: C.textMut, marginLeft: 10 }}>
                            {e.bookmaker}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                          <span style={{ fontSize: 16, fontWeight: 700, color: C.textPri }}>
                            {Number(e.price).toFixed(2)}
                          </span>
                          <span style={{
                            fontSize: 13, fontWeight: 700, color: C.green,
                            background: C.greenLt, padding: "2px 9px", borderRadius: 999,
                          }}>
                            +{pct}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  {edges.length > 3 && (
                    <div style={{ fontSize: 12, color: C.textMut, marginTop: 6 }}>
                      +{edges.length - 3} more — see the Edges tab
                    </div>
                  )}
                </div>
              )}

              {/* match-info grid */}
              <div style={{
                background: "#f8fafc", borderRadius: 12,
                padding: "16px 18px", marginBottom: 18,
              }}>
                <SectionHeading>Match Info</SectionHeading>
                <div style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr",
                  gap: "10px 28px", fontSize: 13,
                }}>
                  {[
                    ["Competition", f.comp || "NHL"],
                    ["Kick-off",    fmtDate(f.kickoff_utc)],
                    ["Home",        f.home_team],
                    ["Away",        f.away_team],
                  ].map(([label, val]) => (
                    <div key={label}>
                      <div style={{ color: C.textMut, marginBottom: 2 }}>{label}</div>
                      <div style={{ color: C.textPri, fontWeight: 600 }}>{val}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* "coming soon" banner */}
              <div style={{
                background: C.accentLight,
                border: "1px solid #bae6fd",
                borderRadius: 10,
                padding: "11px 15px",
                fontSize: 13, color: C.accentDark, lineHeight: 1.5,
              }}>
                Deeper NHL analysis — player props, xG on ice, and AI previews — is coming soon.
                Live edge scanning and market odds are active now.
              </div>
            </div>
          )}

          {/* ═══════════ EDGES ═══════════ */}
          {tab === "Edges" && (
            <div>
              <SectionHeading>All Edges</SectionHeading>
              {edges.length === 0 ? (
                <EmptyState text="No edges detected for this game yet." />
              ) : (
                <div>
                  {edges.map((e, i) => {
                    const pct  = (Number(e.edge) * 100).toFixed(1);
                    const pos  = Number(e.edge) > 0;
                    return (
                      <div key={i} style={{
                        background: C.cardBg,
                        border: `1px solid ${C.rinkLine}`,
                        borderRadius: 12,
                        padding: 16,
                        marginBottom: 12,
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: e.why ? 8 : 0 }}>
                          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            <span style={{ fontWeight: 700, color: C.textPri, fontSize: 15 }}>
                              {e.market}
                            </span>
                            <span style={{ color: C.textSub, fontSize: 13 }}>
                              @ {Number(e.price).toFixed(2)}
                            </span>
                          </div>
                          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            <span style={{
                              color: pos ? C.green : C.red,
                              fontWeight: 700,
                              background: pos ? C.greenLt : C.redLt,
                              padding: "4px 12px",
                              borderRadius: 999,
                              fontSize: 14,
                            }}>
                              {pos ? "+" : ""}{pct}%
                            </span>
                            <span style={{ color: C.textMut, fontSize: 13 }}>
                              {e.bookmaker}
                            </span>
                          </div>
                        </div>
                        {e.why && (
                          <div style={{
                            fontSize: 13,
                            color: C.textSub,
                            lineHeight: 1.5,
                            marginTop: 8,
                            paddingTop: 8,
                            borderTop: `1px solid ${C.accentLight}`,
                          }}>
                            💡 {e.why}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ═══════════ ODDS ═══════════ */}
          {tab === "Odds" && (
            <div>
              <SectionHeading>All Odds</SectionHeading>
              {Object.keys(groupedOdds).length === 0 ? (
                <EmptyState text="No odds captured yet." />
              ) : (
                Object.entries(groupedOdds).map(([book, rows]) => (
                  <div key={book} style={{ marginBottom: 22 }}>
                    {/* bookmaker pill */}
                    <span style={{
                      display: "inline-block",
                      background: C.accentLight, color: C.accentDark,
                      fontSize: 12, fontWeight: 700,
                      padding: "4px 12px", borderRadius: 999,
                      textTransform: "capitalize", marginBottom: 8,
                    }}>
                      {book}
                    </span>

                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                        <thead>
                          <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                            {["Market", "Price", "Last seen"].map((h, i) => (
                              <th key={h} style={{
                                padding: "6px 10px", color: C.textMut,
                                fontWeight: 600, fontSize: 11,
                                textTransform: "uppercase", letterSpacing: 0.5,
                                textAlign: i === 1 ? "right" : "left",
                              }}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((o, idx) => {
                            const seen = o.last_seen
                              ? (o.last_seen.endsWith("Z") ? o.last_seen : o.last_seen + "Z")
                              : "";
                            return (
                              <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                <td style={{ padding: "9px 10px", color: C.textPri, fontWeight: 500 }}>
                                  {o.market}
                                </td>
                                <td style={{ padding: "9px 10px", textAlign: "right", color: C.textPri, fontWeight: 700 }}>
                                  {Number(o.price).toFixed(2)}
                                </td>
                                <td style={{ padding: "9px 10px", color: C.textMut, fontSize: 12 }}>
                                  {seen ? new Date(seen).toLocaleString("en-GB") : "—"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ═══════════ STATS ═══════════ */}
          {tab === "Stats" && (
            <div>
              <SectionHeading>Team Statistics</SectionHeading>
              {!data.home_stats && !data.away_stats ? (
                <EmptyState text="Team statistics will be available soon." />
              ) : (
                <div style={{ display: "grid", gap: 16 }}>
                  {/* Home Team Stats */}
                  {data.home_stats && (
                    <div style={{
                      background: C.cardBg,
                      border: `1px solid ${C.rinkLine}`,
                      borderRadius: 12,
                      padding: 16,
                    }}>
                      <div style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: C.accentDark,
                        marginBottom: 12,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}>
                        {f.home_logo && (
                          <img src={f.home_logo} alt="" width={20} height={20} style={{ objectFit: "contain" }} />
                        )}
                        {f.home_team}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 13 }}>
                        <div>
                          <div style={{ color: C.textMut, marginBottom: 2 }}>Goals For/Game</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: C.textPri }}>
                            {data.home_stats.gf_avg?.toFixed(2) || "—"}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: C.textMut, marginBottom: 2 }}>Goals Against/Game</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: C.textPri }}>
                            {data.home_stats.ga_avg?.toFixed(2) || "—"}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: C.textMut, marginBottom: 2 }}>Win Rate</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: C.textPri }}>
                            {data.home_stats.win_rate ? `${(data.home_stats.win_rate * 100).toFixed(1)}%` : "—"}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: C.textMut, marginBottom: 2 }}>Goal Diff/Game</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: data.home_stats.gd_per_game >= 0 ? C.green : C.red }}>
                            {data.home_stats.gd_per_game ? data.home_stats.gd_per_game.toFixed(2) : "—"}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Away Team Stats */}
                  {data.away_stats && (
                    <div style={{
                      background: C.cardBg,
                      border: `1px solid ${C.rinkLine}`,
                      borderRadius: 12,
                      padding: 16,
                    }}>
                      <div style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: C.accentDark,
                        marginBottom: 12,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}>
                        {f.away_logo && (
                          <img src={f.away_logo} alt="" width={20} height={20} style={{ objectFit: "contain" }} />
                        )}
                        {f.away_team}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 13 }}>
                        <div>
                          <div style={{ color: C.textMut, marginBottom: 2 }}>Goals For/Game</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: C.textPri }}>
                            {data.away_stats.gf_avg?.toFixed(2) || "—"}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: C.textMut, marginBottom: 2 }}>Goals Against/Game</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: C.textPri }}>
                            {data.away_stats.ga_avg?.toFixed(2) || "—"}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: C.textMut, marginBottom: 2 }}>Win Rate</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: C.textPri }}>
                            {data.away_stats.win_rate ? `${(data.away_stats.win_rate * 100).toFixed(1)}%` : "—"}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: C.textMut, marginBottom: 2 }}>Goal Diff/Game</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: data.away_stats.gd_per_game >= 0 ? C.green : C.red }}>
                            {data.away_stats.gd_per_game ? data.away_stats.gd_per_game.toFixed(2) : "—"}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ═══════════ LINEUPS (placeholder) ═══════════ */}
          {tab === "Lineups" && (
            <div style={{ textAlign: "center", padding: "44px 20px" }}>
              <div style={{ fontSize: 48, lineHeight: 1, marginBottom: 14 }}>🏒</div>
              <div style={{ fontSize: 19, fontWeight: 700, color: C.textPri, marginBottom: 8 }}>
                Player Stats Coming Soon
              </div>
              <div style={{
                fontSize: 14, color: C.textMut, maxWidth: 380,
                margin: "0 auto", lineHeight: 1.6,
              }}>
                Season totals, game-log breakdowns, and player-prop markets
                will be available once the Hockey API player endpoints are
                fully wired in.
              </div>
            </div>
          )}

        </div>{/* end tab-content card */}
      </div>{/* end scrollable content */}
    </div>
  );
}
